import { useState } from 'react';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { POST_SESSION_PRIMARY_BG } from '../../../components/session/postSessionCard';
import { ContentCard } from '../../../components/common/ContentCard';
import { CheckCircle, Target } from 'react-feather';
import { participantPostSessionApi } from '../../../services/api/participantPostSession';

const TITLE_COLOR = '#1A1A2E';
const LABEL_COLOR = '#374151';
const FIELD_BORDER = '#E5E7EB';
const FIELD_TEXT = '#374151';
// Read-mode commitment blockquote (Figma: gray fill + navy left accent).
const COMMITMENT_BG = '#F9FAFB';
const COMMITMENT_TEXT = '#374151';
// "Edit Commitment" secondary text link (Figma teal, matching MED-1057).
const EDIT_LINK_COLOR = '#2D7B77';
// MED-1216 — coach-attribution prompt color (amber accent).
const COACH_ATTRIBUTION_COLOR = '#B45309';

interface Props {
  sessionId: string;
  initialCommitmentText: string;
  /**
   * MED-1216 / AC8 — true when the coach entered this commitment and the
   * participant has not yet saved their own. Defaults the card to an editable
   * review state ("noted by your coach, please review and update if needed").
   */
  authoredByCoach: boolean;
  saved: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  /** Called after a successful Save (parent finalizes + collapses the step). */
  onSaved: (commitmentText: string) => void;
}

/**
 * Step 3 — Finalize Commitment card (MED-806 AC12-AC13; polished by MED-1058).
 * Conditionally rendered by the parent when commitment tracking is enabled.
 *
 * The sibling of the Review Session Notes card (MED-1057): the same
 * read/edit-first treatment over the carried-over commitment text. The
 * commitment is pre-filled from the in-session step (E3.3) and persists via the
 * existing MED-805 upsert (single source of truth).
 *
 * MED-1058 polish:
 *   - Read-mode-first when a commitment exists (gray blockquote + "Edit
 *     Commitment" link); opens in edit mode (plain textarea) when empty.
 *   - "Save Commitment" persists and returns to read mode (AC4). It stays
 *     revisable — Save, not Submit.
 *   - Header carries the step name ("Finalize Commitment", no in-card number);
 *     "Saved" check once finalized in this step.
 *   - No AI follow-up line (deferred to MED-893).
 *
 * Completion (the parent's `saved` flag + collapse) is triggered only by the
 * discrete Save here — a pre-filled in-session commitment is NOT complete on
 * load (AC9).
 */
export function FinalizeCommitmentCard({
  sessionId,
  initialCommitmentText,
  authoredByCoach,
  saved,
  expanded,
  onToggleExpand,
  onSaved,
}: Props) {
  const [text, setText] = useState(initialCommitmentText);
  // MED-1216 / AC8 — a coach-entered value the participant hasn't confirmed yet.
  // Cleared once the participant saves (self-save transfers ownership, AC5).
  const [coachAttributed, setCoachAttributed] = useState(
    authoredByCoach && initialCommitmentText.trim().length > 0
  );
  // Read-mode-first when the participant's own commitment already exists; edit
  // mode when empty (AC3) OR when reviewing a coach-entered value (AC8).
  const [editing, setEditing] = useState(
    initialCommitmentText.trim().length === 0 ||
      (authoredByCoach && initialCommitmentText.trim().length > 0)
  );
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    setJustSaved(false);
    try {
      const result = await participantPostSessionApi.upsertCommitment(
        sessionId,
        text
      );
      setText(result.commitment_text);
      setJustSaved(true);
      setCoachAttributed(false); // AC5 — now the participant's own.
      setEditing(false); // AC4 — Save returns to the read-mode display.
      onSaved(result.commitment_text);
    } catch (err) {
      const e = err as { message?: string };
      setError(e?.message ?? 'Failed to save commitment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasCommitment = text.trim().length > 0;

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      data-testid="card-finalize-commitment"
      data-expanded={expanded ? 'true' : 'false'}
      data-saved={saved ? 'true' : 'false'}
      header={
        <UnstyledButton
          onClick={onToggleExpand}
          style={{ width: '100%' }}
          aria-expanded={expanded}
          data-testid="card-finalize-commitment-header"
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap={8} align="center" wrap="nowrap">
              <Target
                size={18}
                color={TITLE_COLOR}
                aria-hidden="true"
                style={{ marginTop: -4 }}
              />
              <Text fz={15} fw={700} c={TITLE_COLOR}>
                Finalize Commitment
              </Text>
              {saved && (
                <Group gap={4} c="medleyGreen">
                  <CheckCircle size={14} />
                  <Text size="xs" fw={600} tt="uppercase">
                    Saved
                  </Text>
                </Group>
              )}
            </Group>
          </Group>
        </UnstyledButton>
      }
    >
      {expanded && (
        <Stack gap="md">
          {editing ? (
            <>
              <Stack gap={6}>
                {coachAttributed ? (
                  // AC8 — the label IS the review prompt for a coach-entered value.
                  <Text
                    fz={13}
                    fw={600}
                    c={COACH_ATTRIBUTION_COLOR}
                    data-testid="commitment-coach-attribution"
                  >
                    Noted by your coach, please review and update if needed
                  </Text>
                ) : (
                  <Text fz={13} fw={600} c={LABEL_COLOR}>
                    What commitment are you taking from this session?
                  </Text>
                )}
                <Textarea
                  placeholder="Write the commitment you’re holding yourself to…"
                  value={text}
                  onChange={e => setText(e.currentTarget.value)}
                  autosize
                  minRows={3}
                  data-testid="commitment-textarea"
                  styles={{
                    input: {
                      borderColor: FIELD_BORDER,
                      borderRadius: 8,
                      color: FIELD_TEXT,
                      fontSize: 13,
                      lineHeight: 1.55,
                    },
                  }}
                />
              </Stack>
              {error && (
                <Alert
                  color="red"
                  variant="light"
                  radius="md"
                  data-testid="commitment-save-error"
                >
                  {error}
                </Alert>
              )}
              <Group justify="space-between" align="center" wrap="nowrap">
                {/* Stable left slot keeps the button right-aligned while the
                    save indicator is idle. */}
                <Box>
                  <SaveStatus saving={saving} saved={justSaved} />
                </Box>
                {/* AC1 — Save disabled until non-empty; tooltip explains why.
                    The Box wrapper lets the tooltip show while disabled. */}
                <Tooltip
                  label="Enter a commitment before saving."
                  disabled={hasCommitment || saving}
                  withArrow
                  position="top"
                >
                  <Box style={{ display: 'inline-block' }}>
                    <Button
                      onClick={handleSave}
                      loading={saving}
                      disabled={!hasCommitment}
                      data-testid="commitment-save"
                      styles={{
                        root: {
                          backgroundColor: POST_SESSION_PRIMARY_BG,
                          color: '#FFFFFF',
                        },
                      }}
                    >
                      Save Commitment
                    </Button>
                  </Box>
                </Tooltip>
              </Group>
            </>
          ) : (
            <>
              {/* AC3 — read-mode display of the commitment (gray blockquote with
                  a navy left accent, matching the Figma). */}
              <Box
                style={{
                  backgroundColor: COMMITMENT_BG,
                  borderLeft: `3px solid ${POST_SESSION_PRIMARY_BG}`,
                  borderRadius: '0 8px 8px 0',
                  padding: '13px 16px 12px 19px',
                }}
              >
                <Text
                  style={{
                    color: COMMITMENT_TEXT,
                    fontSize: 14,
                    lineHeight: 1.65,
                    whiteSpace: 'pre-wrap',
                  }}
                  data-testid="commitment-readonly"
                >
                  {text}
                </Text>
              </Box>
              <Group justify="flex-end" align="center">
                <Anchor
                  component="button"
                  type="button"
                  onClick={() => setEditing(true)}
                  fz={13}
                  fw={500}
                  c={EDIT_LINK_COLOR}
                  data-testid="commitment-edit"
                >
                  Edit Commitment
                </Anchor>
              </Group>
            </>
          )}
        </Stack>
      )}
    </ContentCard>
  );
}

/**
 * MED-1058 AC5 — card-level Saving… / Saved status (no timestamp), matching the
 * sibling cards: muted "Saving…" while the upsert is in flight, then a
 * green-dot "Saved".
 */
function SaveStatus({ saving, saved }: { saving: boolean; saved: boolean }) {
  if (saving) {
    return (
      <Text size="xs" c="dimmed">
        Saving…
      </Text>
    );
  }
  if (saved) {
    return (
      <Group gap={6} align="center" wrap="nowrap">
        <Box
          w={8}
          h={8}
          style={{
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-medleyGreen-6)',
          }}
        />
        <Text size="xs" c="dimmed">
          Saved
        </Text>
      </Group>
    );
  }
  return null;
}

export default FinalizeCommitmentCard;
